"use client";

import React, { ReactNode } from "react";
import Link from "next/link";
import { useInfiniteLGSMatchedCards } from "modules/lgs/hooks/useInfiniteMatchedCards";
import { useLGSUnifiedFilters } from "modules/lgs/hooks/useLGSUnifiedFilters";
import TradeSectionTitle from "modules/lgs/components/stores/table/TradeSectionTitle";
import LGSTradesItemsView from "modules/lgs/components/stores/table/LGSTradesItemsView";
import { useAuth } from "@clerk/nextjs";
import { EmptyStateCard } from "modules/common/components/EmptyStateCard";
import { useQuickJoinStore } from "modules/lgs/hooks/useQuickJoinStore";
import { ArrowRight } from "lucide-react";
import { ICON_SIZE } from "modules/common/components/navigation/Menu";
import { cn } from "utils/utils";
import { ViewIndex } from "modules/common/model/viewTypes";
import { useMediaQuery } from "modules/common/hooks/useMediaQuery";
import MatchedCardsIcon from "modules/common/components/Icons/MatchedCardsIcon";

interface LGSMatchedSectionClientProps {
  storeId: number;
  viewAllLink?: string;
  /** Optional mobile filter bar to display instead of title on mobile */
  mobileFilterBar?: ReactNode;
  /** When true, removes the card wrapper styling on mobile */
  hideCard?: boolean;
  maxItems?: number;
  /** Hide the section title (useful on dedicated pages that have their own heading) */
  hideTitle?: boolean;
  /** Short description displayed below the title */
  description?: string;
  /** View index from SSR cookie — eliminates flash by rendering correct view immediately */
  initialViewIndex?: ViewIndex;
  /** Store name for smart join CTA */
  storeName?: string;
  /** Store coordinates [lng, lat] for centering the store selector dialog */
  storeCoords?: [number, number];
  /** When true, the count is rendered by the page's mainbar, so the desktop
   *  section-title row collapses. The row still renders on mobile, where it
   *  hosts the mobile filter bar. */
  countInMainbar?: boolean;
}

export function LGSMatchedSectionClient({
  storeId,
  viewAllLink,
  mobileFilterBar,
  hideCard,
  maxItems,
  hideTitle,
  description,
  initialViewIndex,
  storeName,
  storeCoords,
  countInMainbar,
}: LGSMatchedSectionClientProps) {
  const { isSignedIn, isLoaded } = useAuth();
  const isDesktop = useMediaQuery("(min-width: 768px)");

  // Smart CTA: check membership for join button
  const { isMember, joinStore, isJoining } = useQuickJoinStore(storeId, {
    storeCoords,
  });

  // Use the SAME unified filters that LGSHeaderTabs controls
  const {
    matchedFilters: lgsMatchedCardsReq,
    activeView,
    hasActiveFilters,
  } = useLGSUnifiedFilters(storeId, "", initialViewIndex);

  // When maxItems is set, reduce perPage to match so the backend doesn't
  // fetch more items than we display. The query key must match the server prefetch.
  const effectiveReq = maxItems
    ? { ...lgsMatchedCardsReq, perPage: maxItems }
    : lgsMatchedCardsReq;

  // Use the existing infinite scroll hook for matched cards
  // React Query will use prefetched data from HydrationBoundary as initial data
  const {
    data: dataResultsMatched,
    isPending: isPendingMatched,
    hasNextPage: hasNextPageMatched,
    isFetchingNextPage: isFetchingNextPageMatched,
    fetchNextPage: fetchNextPageMatched,
  } = useInfiniteLGSMatchedCards(effectiveReq, {
    // Compact sections only exist in the CSS-hidden desktop overview tree.
    // Dedicated pages have no maxItems limit and remain enabled at every size.
    enabled: !maxItems || isDesktop,
  });

  // Helper function to check if data is empty (same as original LGSContent)
  const isDataEmpty = (data: any) => {
    if (!data || !data.pages || data.pages.length === 0) return true;
    const firstPage = data.pages[0];
    return !firstPage || !firstPage.items || firstPage.items.length === 0;
  };

  // If auth is still loading, show loading state (will be handled by isPending below)
  // If user is not signed in (and auth is loaded), show sign-up message
  if (isLoaded && !isSignedIn) {
    return (
      <div className="flex h-full flex-col">
        <TradeSectionTitle
          countInMainbar={countInMainbar}
          title={hideTitle ? undefined : "Matches"}
          description={description}
          type="match"
          leftContent={mobileFilterBar}
        />
        <div className="flex flex-1 items-center justify-center overflow-hidden p-2">
          <EmptyStateCard
            variant="signed-out"
            icon={<MatchedCardsIcon className="h-8 w-8" />}
            title="Sign up"
            description="Sign in to see matches and connect with players at this store."
            showWaitlistCTA={true}
          />
        </div>
      </div>
    );
  }

  const isEffectivelyPending = isPendingMatched;
  const hasData = !isDataEmpty(dataResultsMatched) || isPendingMatched;
  const hasAdditionalMatches = !!maxItems && hasNextPageMatched;

  // Determine empty state message based on filter state
  // Prioritize showing filter message when filters are active
  const emptyTitle = hasActiveFilters ? "No Results" : "No Matched Cards Yet";
  const emptyDescription = hasActiveFilters
    ? "No cards match your current filters. Try adjusting your search or filters."
    : "Add cards to your wants list and we'll notify you when players at this store have them for trade.";

  return (
    <div className="flex h-full flex-col">
      <TradeSectionTitle
        countInMainbar={countInMainbar}
        title={hideTitle ? undefined : "Matches"}
        description={description}
        type="match"
        viewAllLink={hasAdditionalMatches ? viewAllLink : undefined}
        leftContent={mobileFilterBar}
        isLoading={isEffectivelyPending}
        sticky={!!maxItems}
      />
      <div
        className={cn(
          "flex-1 overflow-hidden",
          (!hasData || !isSignedIn) && "flex items-center justify-center"
        )}
      >
        {hasData ? (
          <LGSTradesItemsView
            data={dataResultsMatched}
            isPending={isEffectivelyPending}
            hasNextPage={hasNextPageMatched}
            isFetchingNextPage={isFetchingNextPageMatched}
            fetchNextPage={fetchNextPageMatched}
            isMatchedTrades={true}
            activeView={activeView}
            maxItems={maxItems}
          />
        ) : (
          <EmptyStateCard
            variant="compact"
            icon={<MatchedCardsIcon className="text-primary h-8 w-8" />}
            title={emptyTitle}
            description={emptyDescription}
            ctaText={
              hasActiveFilters
                ? undefined
                : !isMember && storeName
                  ? `Join ${storeName}`
                  : "Add cards to your trade lists"
            }
            ctaLink={
              hasActiveFilters || (!isMember && storeName)
                ? undefined
                : "/x/lists"
            }
            onCtaClick={
              !hasActiveFilters && !isMember && storeName
                ? () => joinStore()
                : undefined
            }
            ctaLoading={isJoining}
          />
        )}
      </div>
      {hasAdditionalMatches && viewAllLink && (
        <div className="flex justify-center pb-1 pt-3">
          <Link
            href={viewAllLink}
            className="btn btn-soft btn-sm btn-primary gap-1"
          >
            View all
            <ArrowRight size={ICON_SIZE} />
          </Link>
        </div>
      )}
    </div>
  );
}
